import React, { useCallback } from "react";
import { Button } from "@showitinc/showit-wc";

import { useSiteState } from "../hooks/query/useSite";
import useRendererLock from "../hooks/query/useRendererLock.mjs";
import SitePublishActions from "../actions/SitePublishActions";
import SiteSettingsActions from "../actions/SiteSettingsActions";
import { TAB_UPGRADE } from "../stores/SiteSettingsStore";
import uiPrefix from "../utils/UiPrefixUtils";
import UpgradedIcon from "./UpgradedIcon";
import HelpTooltip from "./HelpTooltip";
import styles from "./PublishSettingsButton.module.css";

/**
 * Button in the Publish modal that leads into Site Settings. Its label depends
 * on where the design sits in the v2 upgrade flow:
 *
 *   - Design has published on v2 at least once, is not yet locked, and is
 *     otherwise eligible → the button reads "Upgrade" (with the upgraded badge
 *     as a prefix icon and an adjacent help tooltip) and jumps the customer to
 *     the Upgrade tab of Site Settings.
 *   - Otherwise → the button reads "Edit Settings" and opens Site Settings on
 *     its default tab (Site Information).
 *
 * Once the design is locked, the Upgrade tab is gone from Site Settings and
 * the button reverts to "Edit Settings".
 *
 * @returns {React.ReactElement}
 */
const PublishSettingsButton = () => {
  const siteId = useSiteState().get("site_design_id");
  const { eligibility } = useRendererLock(siteId, { enabled: siteId != null });

  const showUpgrade = eligibility?.eligible ?? false;

  const onClick = useCallback(() => {
    SitePublishActions.closeDialog();
    if (showUpgrade) {
      SiteSettingsActions.openDialogWithSection(TAB_UPGRADE);
    } else {
      SiteSettingsActions.openDialog();
    }
  }, [showUpgrade]);

  if (!showUpgrade) {
    return (
      <Button
        variant="minimal"
        size="lg"
        data-e2e={uiPrefix("modal-update-settings-btn")}
        className={uiPrefix("modal-update-settings-btn")}
        onClick={onClick}
      >
        Edit Settings
      </Button>
    );
  }

  return (
    <div className={styles["upgrade-group"]}>
      <Button
        variant="minimal"
        size="lg"
        data-e2e={uiPrefix("modal-update-settings-btn")}
        className={uiPrefix("modal-update-settings-btn")}
        onClick={onClick}
      >
        <span slot="icon-prefix">
          <UpgradedIcon className={styles["upgrade-icon"]} />
        </span>
        Upgrade
      </Button>
      <HelpTooltip tooltipKey="upgrade-cta" placement="top-left" />
    </div>
  );
};

export default PublishSettingsButton;
